package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable
import kotlin.math.abs
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.round
import kotlin.math.sin
import kotlin.math.sqrt

/**
 * A point on the map in decimal degrees (WGS 84), e.g. where a workplace is (SH-110). Stored as
 * numbers (`workplaces/{id}.location`) so a radius check (SH-106) needs no parsing.
 */
@Serializable
data class GeoLocation(val latitude: Double, val longitude: Double) {
    init {
        require(isValid(latitude, longitude)) { "Not a location: $latitude, $longitude" }
    }

    /** Rounded to [DECIMALS] places (about 11 cm): what is stored and shown. */
    fun rounded(): GeoLocation = GeoLocation(roundTo(latitude), roundTo(longitude))

    /** "54.348200, 18.653300": the form a person types or pastes, and the legacy `geoplace` string. */
    fun format(): String = "${formatDegrees(latitude)}, ${formatDegrees(longitude)}"

    /** Great-circle distance in metres (haversine); good to well under a metre at a workplace's scale. */
    fun distanceMetersTo(other: GeoLocation): Double {
        val lat1 = latitude.toRadians()
        val lat2 = other.latitude.toRadians()
        val dLat = lat2 - lat1
        val dLng = (other.longitude - longitude).toRadians()
        val h = sin(dLat / 2).pow(2) + cos(lat1) * cos(lat2) * sin(dLng / 2).pow(2)
        return 2 * EARTH_RADIUS_METERS * asin(sqrt(h.coerceIn(0.0, 1.0)))
    }

    /** Within [toleranceDegrees] of [other] on both axes (the same point after rounding). */
    fun isSameAs(other: GeoLocation, toleranceDegrees: Double = 1e-6): Boolean =
        abs(latitude - other.latitude) <= toleranceDegrees && abs(longitude - other.longitude) <= toleranceDegrees

    companion object {
        const val DECIMALS = 6
        private const val EARTH_RADIUS_METERS = 6_371_008.8

        fun isValid(latitude: Double, longitude: Double): Boolean =
            latitude.isFinite() && longitude.isFinite() && latitude in -90.0..90.0 && longitude in -180.0..180.0

        /** A location, or null when the numbers are out of range. */
        fun of(latitude: Double, longitude: Double): GeoLocation? =
            if (isValid(latitude, longitude)) GeoLocation(latitude, longitude) else null

        /**
         * Reads "latitude, longitude" as people paste it from a map: "54.3482, 18.6533",
         * "54.3482,18.6533", "54.3482 18.6533", "54.3482°, 18.6533°" or with decimal commas
         * ("54,3482; 18,6533" or "54,3482, 18,6533"). Null when it is not two numbers in range.
         */
        fun parse(text: String?): GeoLocation? {
            val cleaned = text?.replace("°", "")?.trim().orEmpty()
            if (cleaned.isEmpty()) return null
            val parts: List<String> = when {
                ';' in cleaned -> cleaned.split(';')
                else -> {
                    val byComma = cleaned.split(',')
                    when (byComma.size) {
                        2 -> byComma
                        // Decimal commas on both numbers: "54,3482, 18,6533".
                        4 -> listOf("${byComma[0]}.${byComma[1]}", "${byComma[2]}.${byComma[3]}")
                        else -> cleaned.split(' ', '\t').filter { it.isNotBlank() }
                    }
                }
            }
            if (parts.size != 2) return null
            val latitude = parts[0].toDegrees() ?: return null
            val longitude = parts[1].toDegrees() ?: return null
            return of(latitude, longitude)
        }

        private fun String.toDegrees(): Double? {
            val number = trim().replace(',', '.')
            // Plain decimal numbers only (no "1e5", no "NaN"): digits, one dot, an optional sign.
            if (number.isEmpty() || number.count { it == '.' } > 1) return null
            val digits = number.removePrefix("-").removePrefix("+")
            if (digits.isEmpty() || digits == "." || digits.any { it != '.' && !it.isDigit() }) return null
            return number.toDoubleOrNull()
        }

        private fun Double.toRadians(): Double = this * kotlin.math.PI / 180.0

        private val SCALE = 10.0.pow(DECIMALS)

        private fun roundTo(value: Double): Double = round(value * SCALE) / SCALE

        /** Fixed [DECIMALS] places without platform formatting (common code, no String.format). */
        private fun formatDegrees(value: Double): String {
            val scaled = round(abs(value) * SCALE).toLong()
            val whole = scaled / SCALE.toLong()
            val fraction = (scaled % SCALE.toLong()).toString().padStart(DECIMALS, '0')
            val sign = if (value < 0 && scaled != 0L) "-" else ""
            return "$sign$whole.$fraction"
        }
    }
}

/**
 * Where a workplace is, from what is stored: the `location` numbers (SH-110) or, in documents
 * saved before, the `geoplace` "lat, lng" text. New apps write both, the same point; when they
 * differ an older app changed only `geoplace` afterwards, so that one is newer.
 */
fun resolveStoredLocation(location: GeoLocation?, geoplace: String?): GeoLocation? {
    val legacy = GeoLocation.parse(geoplace)
    return when {
        location == null -> legacy
        legacy == null -> location
        legacy.isSameAs(location) -> location
        else -> legacy
    }
}
